import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from '@/lib/toast';

const EMPTY_EXPERT = {
    id: null,
    name: '',
    bio: '',
    specializations: [],
    rating: 5.0,
    years_experience: 0,
    image_url: '',
    tiers: [
        { name: 'Limited scope', duration_min: 30,  questions: 2,    amount_inr: 6399,  scope_label: 'Limited scope' },
        { name: 'Good scope',    duration_min: 60,  questions: 4,    amount_inr: 10599, scope_label: 'Good scope' },
        { name: 'Full scope',    duration_min: 180, questions: null, amount_inr: 16999, scope_label: 'Full scope' },
    ],
};

export default function AdminExperts() {
    const [experts, setExperts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/experts');
            setExperts(data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startNew = () => setEditing({ ...EMPTY_EXPERT });
    const startEdit = (e) => setEditing(JSON.parse(JSON.stringify(e)));

    const save = async () => {
        const body = {
            name: editing.name?.trim(),
            bio: editing.bio?.trim(),
            specializations: typeof editing.specializations === 'string'
                ? editing.specializations.split(',').map(s => s.trim()).filter(Boolean)
                : (editing.specializations || []),
            rating: Number(editing.rating || 0),
            years_experience: parseInt(editing.years_experience || 0, 10),
            image_url: editing.image_url || null,
            tiers: editing.tiers.map(t => ({
                name: t.name,
                duration_min: parseInt(t.duration_min, 10),
                questions: t.questions === '' || t.questions === null ? null : parseInt(t.questions, 10),
                amount_inr: parseFloat(t.amount_inr),
            })),
        };
        if (!body.name || body.name.length < 2) return toast.error('Name required');
        if (!body.bio || body.bio.length < 10) return toast.error('Bio must be at least 10 chars');
        try {
            if (editing.id) {
                const { data } = await api.put(`/admin/experts/${editing.id}`, body);
                setExperts(experts.map(e => e.id === editing.id ? data : e));
                toast.success('Expert updated');
            } else {
                const { data } = await api.post('/admin/experts', body);
                setExperts([data, ...experts]);
                toast.success('Expert created');
            }
            setEditing(null);
        } catch (e) {
            toast.error(readErr(e, 'Save failed'));
        }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/admin/experts/${confirmDel.id}`);
            setExperts(experts.filter(e => e.id !== confirmDel.id));
            toast.success('Expert deleted');
            setConfirmDel(null);
        } catch (e) {
            toast.error(readErr(e, 'Delete failed'));
        }
    };

    const updateTier = (idx, field, val) => {
        setEditing(ed => ({
            ...ed,
            tiers: ed.tiers.map((t, i) => i === idx ? { ...t, [field]: val } : t),
        }));
    };

    const addTier = () => setEditing(ed => ({
        ...ed,
        tiers: [...ed.tiers, { name: '', duration_min: 30, questions: null, amount_inr: 0 }],
    }));

    const removeTier = (idx) => setEditing(ed => ({
        ...ed,
        tiers: ed.tiers.filter((_, i) => i !== idx),
    }));

    return (
        <div className="p-6 lg:p-10 space-y-6" data-testid="admin-experts">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Experts</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2">{experts.length} astrologers</h1>
                </div>
                <Button onClick={startNew} data-testid="add-expert" className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm">
                    <Plus size={14} className="mr-2" /> Add expert
                </Button>
            </div>

            {loading ? (
                <div className="text-slate-500">Loading…</div>
            ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {experts.map(e => (
                        <Card key={e.id} className="rounded-sm bg-elevated border-marigold/10 p-5 flex gap-4" data-testid={`expert-card-${e.id}`}>
                            <div className="w-16 h-16 rounded-sm overflow-hidden bg-obsidian border border-marigold/15 flex-shrink-0">
                                {e.image_url ? <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" /> : null}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-serif text-lg text-parchment truncate">{e.name}</div>
                                    <div className="flex gap-1">
                                        <button onClick={() => startEdit(e)} data-testid={`edit-${e.id}`} className="p-1.5 text-slate-400 hover:text-parchment">
                                            <Pencil size={14} />
                                        </button>
                                        <button onClick={() => setConfirmDel(e)} data-testid={`delete-${e.id}`} className="p-1.5 text-slate-400 hover:text-rose-400">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="text-xs text-slate-500 mt-1">★ {e.rating} · {e.years_experience}y</div>
                                <div className="text-xs text-slate-400 mt-2 line-clamp-2">{e.bio}</div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {(e.specializations || []).map(s => (
                                        <span key={s} className="text-[10px] uppercase tracking-wider bg-marigold/10 text-marigold border border-marigold/20 px-2 py-0.5 rounded-sm">{s}</span>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Edit / create dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-surface border-marigold/15 rounded-sm max-w-2xl max-h-[85vh] overflow-auto" data-testid="expert-form-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl text-parchment">
                            {editing?.id ? 'Edit expert' : 'New expert'}
                        </DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
                                    <Input
                                        value={editing.name}
                                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                        data-testid="expert-name"
                                        className="bg-obsidian border-marigold/20 text-parchment rounded-sm mt-2"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-slate-400">Image URL</Label>
                                    <Input
                                        value={editing.image_url || ''}
                                        onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                                        className="bg-obsidian border-marigold/20 text-parchment rounded-sm mt-2"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-slate-400">Rating</Label>
                                    <Input
                                        type="number" step="0.1" min="0" max="5"
                                        value={editing.rating}
                                        onChange={(e) => setEditing({ ...editing, rating: e.target.value })}
                                        className="bg-obsidian border-marigold/20 text-parchment rounded-sm mt-2"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-slate-400">Years experience</Label>
                                    <Input
                                        type="number" min="0"
                                        value={editing.years_experience}
                                        onChange={(e) => setEditing({ ...editing, years_experience: e.target.value })}
                                        className="bg-obsidian border-marigold/20 text-parchment rounded-sm mt-2"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider text-slate-400">Bio</Label>
                                <Textarea
                                    value={editing.bio}
                                    onChange={(e) => setEditing({ ...editing, bio: e.target.value })}
                                    rows={4}
                                    data-testid="expert-bio"
                                    className="bg-obsidian border-marigold/20 text-parchment rounded-sm mt-2"
                                />
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider text-slate-400">Specializations (comma-separated)</Label>
                                <Input
                                    value={Array.isArray(editing.specializations) ? editing.specializations.join(', ') : editing.specializations}
                                    onChange={(e) => setEditing({ ...editing, specializations: e.target.value })}
                                    className="bg-obsidian border-marigold/20 text-parchment rounded-sm mt-2"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs uppercase tracking-wider text-slate-400">Tiers</Label>
                                    <Button size="sm" variant="outline" onClick={addTier} className="bg-transparent border-marigold/20 text-parchment hover:bg-marigold/10 rounded-sm h-7 px-2 text-xs">
                                        <Plus size={12} className="mr-1" /> Add tier
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {editing.tiers.map((t, idx) => (
                                        <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 bg-obsidian rounded-sm border border-marigold/10">
                                            <div className="col-span-3">
                                                <div className="text-[10px] text-slate-500 uppercase">Name</div>
                                                <Input value={t.name} onChange={(e) => updateTier(idx, 'name', e.target.value)} className="bg-surface border-marigold/15 text-parchment rounded-sm h-8 text-xs" />
                                            </div>
                                            <div className="col-span-2">
                                                <div className="text-[10px] text-slate-500 uppercase">Mins</div>
                                                <Input type="number" value={t.duration_min} onChange={(e) => updateTier(idx, 'duration_min', e.target.value)} className="bg-surface border-marigold/15 text-parchment rounded-sm h-8 text-xs" />
                                            </div>
                                            <div className="col-span-2">
                                                <div className="text-[10px] text-slate-500 uppercase">Q's</div>
                                                <Input type="number" value={t.questions ?? ''} placeholder="∞" onChange={(e) => updateTier(idx, 'questions', e.target.value)} className="bg-surface border-marigold/15 text-parchment rounded-sm h-8 text-xs" />
                                            </div>
                                            <div className="col-span-4">
                                                <div className="text-[10px] text-slate-500 uppercase">INR</div>
                                                <Input type="number" value={t.amount_inr} onChange={(e) => updateTier(idx, 'amount_inr', e.target.value)} className="bg-surface border-marigold/15 text-parchment rounded-sm h-8 text-xs" />
                                            </div>
                                            <div className="col-span-1">
                                                <button onClick={() => removeTier(idx)} className="p-2 text-slate-500 hover:text-rose-400">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} className="bg-transparent border-marigold/20 text-slate-300 rounded-sm">Cancel</Button>
                        <Button onClick={save} data-testid="expert-save" className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm">
                            {editing?.id ? 'Save changes' : 'Create expert'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <DialogContent className="bg-surface border-marigold/15 rounded-sm max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl text-parchment">Delete expert?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-400">
                        This permanently removes <span className="text-parchment">{confirmDel?.name}</span>. Existing bookings remain intact.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDel(null)} className="bg-transparent border-marigold/20 text-slate-300 rounded-sm">Cancel</Button>
                        <Button onClick={doDelete} data-testid="expert-confirm-delete" className="bg-rose-500 text-white hover:bg-rose-600 rounded-sm">Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
